import React, { useEffect, useRef, useMemo, useState, useContext } from "react"
import insightsClient from "search-insights"
import { createAlgoliaInsightsPlugin } from "@algolia/autocomplete-plugin-algolia-insights"

import { createAutocomplete } from "@algolia/autocomplete-core"
import { getAlgoliaResults } from "@algolia/autocomplete-preset-algolia"
import algoliasearch from "algoliasearch/lite"
import useIsMount from "../../hooks/useMount"
import { debouncePromise } from "../../lib/debouncePromise"
import { debounce } from "throttle-debounce"
import ClearIcon from "./ClearIcon"
import Highlight from "./Highlight"
import SearchIcon from "./SearchIcon"
import FeatureTooltip from "../FeatureTooltip"
import AppContext from "../../shared/AppContext"
import SourceTitleHeader from "./SourceTitleHeader"

import "@algolia/autocomplete-theme-classic"
import "./globalSearch.scss"

const indices = [
  { name: "vendors", displayName: "Vendors" },
  { name: "models", displayName: "Models" },
  { name: "products", displayName: "Reports" },
  { name: "articles", displayName: "Articles" },
  { name: "favorites", displayName: "Favorites" },
  { name: "drilldowns", displayName: "Drill Downs" },
]

const getOptions = () => {
  const indexOptions = [{ name: "", displayName: "All" }, ...indices]
  return indexOptions.map((index, i) => {
    return (
      <option key={i} value={index.name}>
        {index.displayName}
      </option>
    )
  })
}

const debounced = debouncePromise((items) => Promise.resolve(items), 400)

const GlobalSearch = (props) => {
  const { algoliaSearchKey, showToolTip, userId } = props

  const searchClient = algoliasearch(
    process.env.ALGOLIA_APP_ID,
    algoliaSearchKey
  )

  insightsClient("init", {
    appId: process.env.ALGOLIA_APP_ID,
    apiKey: algoliaSearchKey,
  })

  const { trackEvent } = useContext(AppContext)

  const [autocompleteState, setAutocompleteState] = useState({
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: "",
    activeItemId: null,
    status: "idle",
  })

  const [selectedIndex, setSelectedIndex] = useState("")
  const [displayToolTip, setDisplayShowToolTip] = useState(false)

  const inputRef = useRef(null)
  const formRef = useRef(null)
  const panelRef = useRef(null)
  const isMounted = useIsMount()

  useEffect(() => {
    if (showToolTip) {
      const viewedToolTip = JSON.parse(
        localStorage.getItem("globalsearchbar.tooltip.viewed")
      )

      if (!viewedToolTip) {
        setDisplayShowToolTip(true)
      }
    }
  }, [])

  useEffect(() => {
    if (isMounted) {
      inputRef.current.focus()
    }
  }, [selectedIndex])

  const trackKeywordSearch = (query) => {
    if (query) {
      trackEvent("Keyword Search from Site Search", {
        keyword: query,
        selectedIndex: selectedIndex
          ? getIndex(selectedIndex).displayName
          : "All",
      })
    }
  }

  const trackDebouncedKeyword = debounce(400, trackKeywordSearch)

  const getHeaderText = (source) =>
    indices.find((index) => index.name === source)

  const getIndex = (indexName) =>
    indices.find((index) => index.name === indexName)

  const algoliaInsightsPlugin = createAlgoliaInsightsPlugin({
    insightsClient,
    onItemsChange({ insights, insightsEvents }) {
      const events = insightsEvents.map((insightsEvent) => ({
        ...insightsEvent,
        eventName: "Viewed from Site Search",
      }))
      insights.viewedObjectIDs(...events)
    },
    onSelect({ insights, insightsEvents }) {
      const events = insightsEvents.map((insightsEvent) => ({
        ...insightsEvent,
        eventName: "Selected Item from Site Search",
        userToken: userId,
      }))

      const { slug, name, id } = events[0].items[0]

      trackEvent("Selected Item from Site Search", {
        keyword: autocompleteState.query,
        selectedIndex: selectedIndex
          ? getIndex(selectedIndex).displayName
          : "All",
        selectedItem: {
          index: events[0].index,
          id,
          name,
          slug,
        },
        itemPanelPosition: events[0].positions[0],
      })

      insights.clickedObjectIDsAfterSearch(...events)
    },
  })

  const autocomplete = useMemo(
    () =>
      createAutocomplete({
        onStateChange({ state }) {
          setAutocompleteState(state)
        },
        insights: true,
        placeholder: autocompleteState.query ? "" : "Search ETR",
        openOnFocus: true,
        plugins: [algoliaInsightsPlugin],
        getSources({ query }) {
          trackDebouncedKeyword(query)
          if (!selectedIndex) {
            return debounced(
              indices.map((index) => {
                const indexLowerCase = index.name
                return {
                  sourceId: `${indexLowerCase}`,
                  getItems() {
                    return getAlgoliaResults({
                      searchClient,
                      clickAnalytics: true,
                      queries: [
                        {
                          indexName: `${process.env.ALGOLIA_INDEX_PREFIX}${indexLowerCase}`,
                          query,
                          params: {
                            hitsPerPage: 5,
                          },
                        },
                      ],
                      templates: {
                        item({ item, html }) {
                          return html`<div>${item.name}</div>`
                        },
                      },
                    })
                  },
                  getItemUrl({ item }) {
                    return item.slug
                  },
                }
              })
            )
          } else {
            return debounced([
              {
                sourceId: `${selectedIndex}`,
                getItems() {
                  return getAlgoliaResults({
                    searchClient,
                    clickAnalytics: true,
                    queries: [
                      {
                        indexName: `${process.env.ALGOLIA_INDEX_PREFIX}${selectedIndex}`,
                        query,
                        params: {
                          hitsPerPage: 15,
                        },
                      },
                    ],
                  })
                },
                getItemUrl({ item }) {
                  return item.slug
                },
              },
            ])
          }
        },
      }),
    [selectedIndex]
  )

  return (
    <div className="global-search-container">
      <div className="aa-Autocomplete" {...autocomplete.getRootProps({})}>
        <div
          className="autocomplete-container"
          data-cy="autocomplete-container"
        >
          <form
            ref={formRef}
            className="aa-Form"
            onBlur={() => {
              autocomplete.setIsOpen(false)
            }}
            {...autocomplete.getFormProps({ inputElement: inputRef.current })}
          >
            <div className="aa-InputWrapperPrefix">
              <select
                value={selectedIndex}
                onChange={(e) => {
                  setSelectedIndex(e.target.value)

                  trackEvent("Filtered by Index from Site Search", {
                    selectedIndex:
                      getIndex(e.target.value).displayName || "All",
                  })
                }}
              >
                {getOptions()}
              </select>
              <SearchIcon className="search-icon" />
            </div>
            <div className="aa-InputWrapper">
              <input
                className="aa-Input"
                ref={inputRef}
                {...autocomplete.getInputProps({
                  inputElement: inputRef.current,
                })}
              />
            </div>
            <div className="aa-InputWrapperSuffix">
              <button
                onClick={() => {
                  if (autocompleteState.query) {
                    trackEvent("Clear Site Search Clicked", {
                      clearedKeyword: autocompleteState.query,
                      selectedIndex: selectedIndex
                        ? getIndex(selectedIndex).displayName
                        : "All",
                    })
                  }
                }}
                className="aa-ClearButton"
                title="Clear"
                type="reset"
              >
                <ClearIcon />
              </button>
            </div>
          </form>

          {autocompleteState.isOpen && (
            <div
              ref={panelRef}
              className={"aa-Panel aa-Panel--desktop"}
              {...autocomplete.getPanelProps({})}
            >
              <div className="aa-PanelLayout aa-Panel--scrollable">
                {autocompleteState.collections.map((collection, index) => {
                  const { source, items } = collection
                  const title =
                    source && source.sourceId
                      ? getHeaderText(source.sourceId).displayName
                      : null
                  return (
                    <section key={`source-${index}`} className="aa-Source">
                      {!!items.length && (
                        <>
                          <SourceTitleHeader headerText={title} />
                          <ul
                            className="aa-List"
                            {...autocomplete.getListProps()}
                          >
                            {items.map((item) => {
                              return (
                                <li
                                  key={item.objectID}
                                  className="aa-Item"
                                  {...autocomplete.getItemProps({
                                    item,
                                    source,
                                  })}
                                >
                                  <a href={item.slug} className="aa-ItemLink">
                                    <div className="aa-ItemContentBody">
                                      <SearchIcon className="search-icon" />
                                      <div className="aa-ItemContentTitle">
                                        <Highlight
                                          hit={item}
                                          attribute="name"
                                        />
                                      </div>
                                      {item.dataset &&
                                      item.dataset !== "DRILLDOWN" ? (
                                        <div className="aa-ItemContentDescription">
                                          <strong>{item.dataset}</strong>{" "}
                                        </div>
                                      ) : null}
                                    </div>
                                  </a>
                                </li>
                              )
                            })}
                          </ul>
                        </>
                      )}
                    </section>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {displayToolTip ? (
        <FeatureTooltip
          title={"New Search Bar"}
          isOpen={(isOpen) => {
            setDisplayShowToolTip(isOpen)

            if (!isOpen) {
              localStorage.setItem(
                "globalsearchbar.tooltip.viewed",
                JSON.stringify(true)
              )
            }
          }}
          content="Explore with our new search bar! Discover vendors, articles and
    more. Simply enter keywords and find relevant results instantly.
    Try it now!"
        />
      ) : null}
    </div>
  )
}

export default GlobalSearch
